'use client';

import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';

import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from '@/components/alter25/carousel/Alter25ProductCarouselArrows';
import { Alter25ProductCarouselThumb } from '@/components/alter25/carousel/Alter25ProductCarouselThumb';
import type { GalleryT } from '@/utils/contentfulTypes';

interface Alter25ProductCarouselProps {
  swabKitProductGallery: GalleryT;
}

export const Alter25ProductCarousel: React.FC<Alter25ProductCarouselProps> = ({
  swabKitProductGallery,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({});
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();

    emblaMainApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaMainApi, onSelect]);

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaMainApi);

  return (
    <div className="mb-6 -mx-4 sm:mx-auto">
      <div className="carousel-embla">
        <div className="embla__viewport" ref={emblaMainRef}>
          <div className="embla__container">
            {swabKitProductGallery.imagesCollection.items.map((carouselItem, index) => (
              <div className="embla__slide" key={carouselItem.url}>
                <Image
                  src={`${carouselItem.url}?fm=webp&w=1160&q=90`}
                  alt="Carousel Item"
                  width={580}
                  height={580}
                  className={clsx('embla__slide__image', 'w-full h-full')}
                  priority={index === 0 ? true : false}
                />
              </div>
            ))}
          </div>
          <div className="embla__buttons block sm:hidden">
            <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
            <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
          </div>
        </div>

        <div className="carousel-embla-thumbs sm:block hidden">
          <div className="embla-thumbs__viewport" ref={emblaThumbsRef}>
            <div className="embla-thumbs__container">
              {swabKitProductGallery.imagesCollection.items.map((carouselItem, index) => (
                <Alter25ProductCarouselThumb
                  key={carouselItem.url}
                  onClick={() => onThumbClick(index)}
                  selected={index === selectedIndex}
                  index={index}
                  carouselItem={carouselItem}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
